import { Box, Divider, Skeleton, Stack, Typography } from "@mui/material";
import TypographyWithMiss from "components/ui/atoms/TypographyWithMiss";
import { InvestmentStatusTag } from "components/ui/atoms/Tag";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import {
  useCommitteeMinutePreview,
  useCommitteeMinutePrint,
} from "./apiCommitteeMinute";

const CommentCell = ({ snapshot, section }) => {
  const get_comment = (snapshot) => {
    if (!snapshot) return undefined;
    if (section.code == "NEW_DEAL_CLOSED")
      return (
        snapshot.reason_to_close ||
        snapshot.comment_summary ||
        snapshot.comment.match("(.*)s*$")[0]
      );
    if (section.code == "INVESTMENT_DECISION")
      return snapshot.comment_investment;
    return snapshot.comment_summary || snapshot.comment.match("(.*)s*$")[0];
  };

  const current_short = get_comment(snapshot);
  const previous_short = get_comment(snapshot.previous_snapshot);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        minWidth: 0,
      }}
    >
      {previous_short !== undefined &&
        previous_short !== current_short &&
        section.code !== "INVESTMENT_DECISION" &&
        false && (
          <TypographyWithMiss
            variant="caption"
            whiteSpace="pre-line"
            sx={{
              textDecorationLine: "line-through",
              wordWrap: "break-word",
              maxWidth: "100%",
            }}
            isMissing={previous_short.trim() === "" || previous_short === null}
          >
            {previous_short}
          </TypographyWithMiss>
        )}
      {current_short !== undefined && (
        <TypographyWithMiss
          variant="caption"
          whiteSpace="pre-line"
          isMissing={current_short.trim() === "" || current_short === null}
        >
          {current_short}
        </TypographyWithMiss>
      )}
    </Box>
  );
};

const OwnerCell = ({ snapshot, section }) => {
  const get_owners = (snapshot) => {
    if (!snapshot) return undefined;
    const deal_owner = snapshot?.deal_owner?.initials || "";
    const secondary_owner = snapshot?.secondary_owner?.initials || "";
    const separator = deal_owner && secondary_owner ? "/" : "";
    return deal_owner + separator + secondary_owner;
  };

  const current_owners = get_owners(snapshot);
  const previous_owners = get_owners(snapshot.previous_snapshot);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        width: 50,
        flexShrink: 0,
      }}
    >
      {previous_owners !== undefined && previous_owners !== current_owners && (
        <TypographyWithMiss
          variant="caption"
          whiteSpace="pre-line"
          sx={{
            textDecorationLine: "line-through",
            wordWrap: "break-word",
            maxWidth: "100%",
          }}
          isMissing={previous_owners.trim() === "" || previous_owners === null}
        >
          {previous_owners}
        </TypographyWithMiss>
      )}
      <TypographyWithMiss
        variant="caption"
        whiteSpace="pre-line"
        isMissing={current_owners.trim() === "" || current_owners === null}
      >
        {current_owners}
      </TypographyWithMiss>
    </Box>
  );
};

const InvestmentStatusCell = ({ snapshot }) => {
  const current = snapshot.investment_status;
  const previous = snapshot.previous_snapshot?.investment_status;

  if (previous === undefined || previous === current)
    return (
      <Box
        sx={{
          display: "flex",
          height: "100%",
          justifyContent: "end",
          alignItems: "center",
          flexShrink: 0,
          ml: "auto !important",
        }}
      >
        <InvestmentStatusTag status={current} />
      </Box>
    );
  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        justifyContent: "end",
        alignItems: "center",
        ml: "auto !important",
      }}
    >
      <InvestmentStatusTag status={previous} />
      <ArrowCircleRightIcon color={"error"} />
      <InvestmentStatusTag status={current} />
    </Box>
  );
};

const NormalCell = ({ value, sx }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        height: "100%",
        ...sx,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const CommitteeMinutePrintListSectionInfo = ({
  sectionInfo: { section, snapshots },
}) => {
  if (snapshots.length == 0) return <></>;

  return (
    <>
      <Typography variant="h6" sx={{ color: "secondary.main" }} mt={2}>
        {section.label}
      </Typography>
      <Divider />
      {snapshots.map((snapshot) => (
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          borderBottom={"1px solid lightgrey"}
          key={snapshot.id}
        >
          <NormalCell
            sx={{ width: 100, flexShrink: 0 }}
            value={snapshot.company.best_name}
          />
          <OwnerCell snapshot={snapshot} />
          <CommentCell snapshot={snapshot} section={section} />
          <InvestmentStatusCell snapshot={snapshot} />
        </Stack>
      ))}
    </>
  );
};

const CommitteeMinutePrintListCondensed = ({ committeeMinuteId }) => {
  const {
    data: committeeMinutePrint,
    isSuccess: committeeMinutePrintIsSuccess,
    isError: committeeMinutePrintIsError,
  } = useCommitteeMinutePrint(committeeMinuteId);

  if (committeeMinutePrintIsError) {
    return <>Error</>;
  }

  if (!committeeMinutePrintIsSuccess) {
    return <Skeleton variant="rectangular" height={40} />;
  }

  return (
    <>
      {committeeMinutePrint.sections.map((sectionInfo, index) => (
        <CommitteeMinutePrintListSectionInfo
          key={index}
          sectionInfo={sectionInfo}
        />
      ))}
    </>
  );
};

export const CommitteeMinutePrintListCondensedPreview = ({ dealFlowId }) => {
  const {
    data: committeeMinutePrint,
    isSuccess: committeeMinutePrintIsSuccess,
    isError: committeeMinutePrintIsError,
  } = useCommitteeMinutePreview(dealFlowId);

  if (committeeMinutePrintIsError) {
    return <>Error</>;
  }

  if (!committeeMinutePrintIsSuccess) {
    return <Skeleton variant="rectangular" height={40} />;
  }

  return (
    <>
      {committeeMinutePrint.sections.map((sectionInfo, index) => (
        <CommitteeMinutePrintListSectionInfo
          key={index}
          sectionInfo={sectionInfo}
        />
      ))}
    </>
  );
};

export default CommitteeMinutePrintListCondensed;
